import { CustomerLead } from '@common';
import { useCustomerLeads, getCustomerLeadsWithFilters } from '@hooks';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Dropdown, Table, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState, useMemo } from 'react';
import '../careersStyles.css';

const { Title, Paragraph } = Typography;

type StatusFilterType = 'All' | 'pending' | 'contacted' | 'qualified' | 'converted' | 'rejected';
type LeadStatus = 'pending' | 'contacted' | 'qualified' | 'converted' | 'rejected';

const STATUS_FILTERS: StatusFilterType[] = ['All', 'pending', 'contacted', 'qualified', 'converted', 'rejected'];
const STATUS_OPTIONS: { key: LeadStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'converted', label: 'Converted' },
  { key: 'rejected', label: 'Rejected' },
];

export const CustomerLeadsPage = () => {
  const { updateCustomerLeadStatus, deleteCustomerLead } = useCustomerLeads();

  const [activeFilter, setActiveFilter] = useState<StatusFilterType>('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>('descend');

  const params = useMemo(() => ({
    page,
    limit,
    search,
    status: activeFilter !== 'All' ? activeFilter : undefined,
    orderBy: sortField,
    orderType: (sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined) as 'asc' | 'desc' | undefined,
  }), [page, limit, search, activeFilter, sortField, sortOrder]);

  const { data, isLoading, refetch } = getCustomerLeadsWithFilters(params);

  useEffect(() => {
    const timeoutId = setTimeout(() => { refetch(); }, 300);
    return () => clearTimeout(timeoutId);
  }, [params, refetch]);

  const leads = (data?.data || []) as CustomerLead[];
  const total = data?.pagination?.totalCount || 0;

  const handleTableChange = (_pagination: TablePaginationConfig, _filters: any, sorter: any) => {
    if (Array.isArray(sorter)) return;

    if (sorter && sorter.field) {
      const field = sorter.field as string;
      const order = sorter.order === 'ascend' ? 'ascend' : sorter.order === 'descend' ? 'descend' : undefined;
      setSortField(field);
      setSortOrder(order);
      setPage(1);
    } else {
      setSortField('createdAt');
      setSortOrder('descend');
      setPage(1);
    }
  };

  const handleStatusChange = (id: string, status: LeadStatus) => {
    updateCustomerLeadStatus.mutate({ id, data: { status } });
  };

  const columns: TableColumnsType<CustomerLead> = [
    {
      title: <span className="font-azonix">CUSTOMER</span>,
      key: 'customer',
      dataIndex: 'firstName',
      render: (_: any, record) => (
        <div>
          <div className="font-coda text-white">{record.firstName} {record.lastName}</div>
          <div className="font-coda text-secondary text-sm">{record.email}</div>
        </div>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">PHONE</span>,
      key: 'phone',
      dataIndex: 'phone',
      render: (phone: string) => <span className="font-coda">{phone}</span>,
    },
    {
      title: <span className="font-azonix">SERVICE ADDRESS</span>,
      key: 'serviceAddress',
      dataIndex: 'serviceAddress',
      render: (addr: string) => <span className="font-coda">{addr}</span>,
    },
    {
      title: <span className="font-azonix">SOURCE</span>,
      key: 'source',
      dataIndex: 'source',
      render: (source: string) => <span className="font-coda">{source}</span>,
    },
    {
      title: <span className="font-azonix">STATUS</span>,
      key: 'status',
      dataIndex: 'status',
      render: (status: LeadStatus, record) => (
        <Dropdown
          menu={{
            items: STATUS_OPTIONS.map(item => ({
              key: item.key,
              label: (
                <span className="font-coda" onClick={() => handleStatusChange(record.id, item.key)}>
                  {item.label}
                </span>
              ),
              className: status === item.key ? 'ant-dropdown-menu-item-selected' : ''
            })),
          }}
          trigger={['click']}
        >
          <Button type="text" className="font-coda !font-normal uppercase text-primary">
            {status}
          </Button>
        </Dropdown>
      ),
      sorter: true,
    },
    {
      title: <span className="font-azonix">CREATED AT</span>,
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (date: string) => <span className="font-coda">{date ? dayjs(date).format('MM/DD/YYYY') : '-'}</span>,
      sorter: true,
    },
    {
      title: <span className="font-azonix">ACTIONS</span>,
      key: 'actions',
      width: 100,
      render: (_: any, record) => (
        <div className="flex gap-2">
          <Tooltip title="Delete Lead">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined className="!text-lg" />}
              onClick={(e) => {
                e.stopPropagation();
                deleteCustomerLead.mutate(record.id);
              }}
              className="!text-red-500 hover:[&_svg]:transition-all hover:[&_svg]:duration-200 hover:[&_svg]:scale-110 hover:[&_svg]:drop-shadow-[0_4px_8px_#ef4444]"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="estabrook-bg pt-25 pb-10 md:pb-20">
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <Title level={1} className="font-azonix !font-normal text-primary text-center tracking-wider pb-4 !mb-0">
            CUSTOMER LEADS
          </Title>
          <div className="max-w-3xl mx-auto !mb-8">
            <Paragraph className="text-center text-lg text-light">
              View and manage all customer leads from the landing page
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="w-full py-12 bg-charcoal">
        <div className="container mx-auto px-4">
          <div>
            <Title level={2} className="font-azonix !font-normal text-white">ALL LEADS</Title>
          </div>
          <Paragraph className="text-white !mb-8">
            Filter leads by status or search by customer.
          </Paragraph>

          <div className="flex flex-wrap justify-start gap-2 mb-6">
            {STATUS_FILTERS.map((filter) => (
              <Button
                key={filter}
                type="default"
                className={`filter-button ${activeFilter === filter ? 'filter-active' : ''}`}
                onClick={() => { setActiveFilter(filter); setPage(1); }}
              >
                {filter === 'All' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SearchOutlined className="!text-[#00ade6]" style={{ color: '#00ade6' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="w-full sm:w-72 h-11 pl-11 pr-4 bg-zinc-800/80 border border-zinc-700 text-white placeholder-zinc-500 font-coda text-sm focus:outline-none focus:border-[#00ade6] focus:ring-1 focus:ring-[#00ade6]/50 transition-all duration-200"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); }}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-white transition-colors cursor-pointer"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 shadow-xl overflow-x-auto hide-scrollbar">
            <Table<CustomerLead>
              columns={columns}
              dataSource={leads}
              rowKey={(record) => record.id}
              loading={isLoading}
              onChange={handleTableChange}
              pagination={{
                current: page,
                pageSize: limit,
                total,
                onChange: (currentPage, pageSize) => {
                  setPage(currentPage);
                  setLimit(pageSize);
                },
                showSizeChanger: true,
                showTotal: (total) => <span className="text-white font-coda">Total <span className="text-white font-coda">{total}</span> leads</span>,
                className: "!bg-transparent [&_.ant-pagination-item]:!bg-zinc-800 [&_.ant-pagination-item]:!border-zinc-700 [&_.ant-pagination-item-active]:!bg-transparent [&_.ant-pagination-item-active]:!border-zinc-700 [&_.ant-pagination-item_a]:!text-white [&_.ant-pagination-item-active_a]:!text-white [&_.ant-pagination-prev_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-next_.ant-pagination-item-link]:!bg-zinc-800 [&_.ant-pagination-prev_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-next_.ant-pagination-item-link]:!border-zinc-700 [&_.ant-pagination-prev_.ant-pagination-item-link]:!text-white [&_.ant-pagination-next_.ant-pagination-item-link]:!text-white",
              }}
              className="[&_.ant-table]:!bg-transparent [&_.ant-table-thead_th]:!bg-zinc-800/50 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-[#00ade6] [&_.ant-table-thead_th]:!font-azonix [&_.ant-table-tbody_td]:!bg-transparent [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white [&_.ant-table-row:hover_td]:!bg-zinc-800/50 [&_.ant-table-placeholder]:!bg-transparent [&_.ant-empty-description]:!text-white min-w-[800px]"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
